import {
  applyPastedTable,
  bindColor,
  brandScale,
  contrastColor,
  insertTableColumn,
  insertTableRow,
  normalizeTable,
  parseTableText,
  removeTableColumn,
  removeTableRow,
  TABLE_CELL_MAX_LENGTH,
  TABLE_MAX_COLUMNS,
  TABLE_MAX_ROWS,
  TABLE_PRESETS,
  type TableAlign,
  type TableElement,
  type TablePreset,
} from "@cva/shared";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ClipboardPaste,
  Columns3,
  Plus,
  Rows3,
  Table2,
  X,
} from "lucide-react";
import { type ClipboardEvent, type ReactNode, useState } from "react";
import { toast } from "sonner";
import { Textarea } from "../components/ui/input";
import { cn } from "../lib/utils";
import { type BrandLink, ColorButton } from "./ColorPicker";
import { addElements } from "./commands";
import { FontPicker } from "./FontPicker";
import type { EditorStore } from "./store";
import { activePageDoc, useEditor, useEditorApi } from "./store";
import { NumberField, Row, Section, Toggle } from "./ui";

/**
 * Table element: presets in the elements panel, content grid (with paste
 * from Excel/CSV), rows/columns and style in the properties panel. All
 * changes go through `store.update` (undo + live collaboration).
 */

// ---------------------------------------------------------------------------
// Inserting
// ---------------------------------------------------------------------------

function insertTable(store: EditorStore, preset: TablePreset) {
  const { brand } = store.getState();
  const doc = activePageDoc(store);
  const element = preset.create();
  const scale = brandScale(doc);
  const width = Math.round(doc.format.width * 0.75);
  const factor = width / element.width;
  element.width = width;
  element.fontSize = Math.max(8, Math.round(28 * scale));
  element.padding = Math.round(element.padding * Math.max(scale, factor * 0.5));
  element.borderWidth = element.borderWidth > 0 ? Math.max(1, Math.round(2 * scale)) : 0;
  if (brand) {
    const body = brand.textStyles.find((style) => style.key === "body");
    if (body) element.fontFamily = body.fontFamily;
    bindColor(element, "color", "text", brand);
    if (bindColor(element, "fill", "primary", brand)) {
      element.headerColor = contrastColor(element.headerFill);
    }
  }
  // Rough height until the canvas measured the real one.
  element.height = Math.round(
    element.cells.length * (element.fontSize * 1.3 + 2 * element.padding),
  );
  element.x = Math.round((doc.format.width - element.width) / 2);
  element.y = Math.round((doc.format.height - element.height) / 2);
  addElements(store, [element]);
}

export function TableInsert() {
  const store = useEditorApi();
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {TABLE_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => insertTable(store, preset)}
            title={`Tabelle „${preset.label}“ einfügen`}
            className="flex flex-col items-center gap-1 rounded-lg border border-zinc-200 px-1 py-2 text-[11px] text-zinc-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
          >
            <Table2 className="size-5" />
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Properties
// ---------------------------------------------------------------------------

const ALIGN_ICONS: Record<TableAlign, typeof AlignLeft> = {
  left: AlignLeft,
  center: AlignCenter,
  right: AlignRight,
};
const NEXT_ALIGN: Record<TableAlign, TableAlign> = {
  left: "center",
  center: "right",
  right: "left",
};
const ALIGN_LABELS: Record<TableAlign, string> = {
  left: "linksbündig",
  center: "zentriert",
  right: "rechtsbündig",
};

export function TableProperties({ element }: { element: TableElement }) {
  const store = useEditorApi();
  const brand = useEditor((s) => s.brand);
  const readOnly = useEditor((s) => s.readOnly);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const set = (recipe: (target: TableElement) => void, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.elements[element.id];
        if (target?.type === "table") recipe(target);
      },
      merge ? { merge: `${element.id}:${merge}` } : undefined,
    );
  const link = (slot: "fill" | "color" | "stroke"): BrandLink | undefined =>
    brand
      ? {
          token: element.brand?.[slot],
          onToken: (key) =>
            set((target) => {
              bindColor(target, slot, key, brand);
            }),
        }
      : undefined;

  /** Excel & co. put tabs and line breaks on the clipboard: fill from this cell on. */
  const pasteInto = (event: ClipboardEvent<HTMLInputElement>, row: number, column: number) => {
    const text = event.clipboardData.getData("text/plain");
    if (!/[\t\n]/.test(text.replace(/\n$/, ""))) return;
    event.preventDefault();
    const rows = parseTableText(text);
    if (rows.length === 0) return;
    set((target) => {
      for (let r = 0; r < rows.length; r++) {
        const targetRow = row + r;
        if (targetRow >= TABLE_MAX_ROWS) break;
        while (target.cells.length <= targetRow) insertTableRow(target);
        const values = rows[r] ?? [];
        for (let c = 0; c < values.length; c++) {
          const targetColumn = column + c;
          if (targetColumn >= TABLE_MAX_COLUMNS) break;
          while (target.columns.length <= targetColumn) insertTableColumn(target);
          target.cells[targetRow]![targetColumn] = values[c] ?? "";
        }
      }
      normalizeTable(target);
    });
  };

  const applyPaste = () => {
    const rows = parseTableText(pasteText);
    if (rows.length === 0) {
      toast.error("Keine Tabelle erkannt. Kopiere Zellen aus Excel oder füge CSV-Text ein.");
      return;
    }
    set((target) => {
      applyPastedTable(target, rows);
    });
    toast.success(`${rows.length} Zeilen × ${rows[0]!.length} Spalten übernommen`);
    setPasteText("");
    setPasteOpen(false);
  };

  const columns = element.columns.length;
  return (
    <>
      <Section title="Tabelle">
        <div className="space-y-1.5">
          <div className="flex gap-1 pl-6">
            {element.columns.map((column, index) => {
              const Icon = ALIGN_ICONS[column.align];
              return (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: columns have no identity
                  key={index}
                  className="flex min-w-0 flex-1 items-center justify-center gap-0.5"
                >
                  <button
                    type="button"
                    disabled={readOnly}
                    title={`Spalte ${index + 1}: ${ALIGN_LABELS[column.align]} (klicken zum Wechseln)`}
                    onClick={() =>
                      set((target) => {
                        const col = target.columns[index];
                        if (col) col.align = NEXT_ALIGN[col.align];
                      })
                    }
                    className="rounded p-0.5 text-zinc-500 hover:bg-zinc-100"
                  >
                    <Icon className="size-3.5" />
                  </button>
                  {columns > 1 && !readOnly ? (
                    <button
                      type="button"
                      title={`Spalte ${index + 1} löschen`}
                      onClick={() => set((target) => void removeTableColumn(target, index))}
                      className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-red-600"
                    >
                      <X className="size-3" />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {element.cells.map((row, rowIndex) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity
              <div key={rowIndex} className="flex items-center gap-1">
                {element.cells.length > 1 && !readOnly ? (
                  <button
                    type="button"
                    title={`Zeile ${rowIndex + 1} löschen`}
                    onClick={() => set((target) => void removeTableRow(target, rowIndex))}
                    className="w-5 shrink-0 rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-red-600"
                  >
                    <X className="size-3" />
                  </button>
                ) : (
                  <span className="w-5 shrink-0" />
                )}
                {element.columns.map((_, column) => (
                  <input
                    // biome-ignore lint/suspicious/noArrayIndexKey: cells have no identity
                    key={column}
                    value={row[column] ?? ""}
                    disabled={readOnly}
                    maxLength={TABLE_CELL_MAX_LENGTH}
                    aria-label={`Zeile ${rowIndex + 1}, Spalte ${column + 1}`}
                    data-table-cell={`${rowIndex}:${column}`}
                    onPaste={(event) => pasteInto(event, rowIndex, column)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      // Enter jumps to the cell below (a new row at the end).
                      event.preventDefault();
                      if (rowIndex === element.cells.length - 1) {
                        set((target) => void insertTableRow(target));
                      }
                      requestAnimationFrame(() =>
                        document
                          .querySelector<HTMLInputElement>(
                            `[data-table-cell="${rowIndex + 1}:${column}"]`,
                          )
                          ?.focus(),
                      );
                    }}
                    onChange={(event) => {
                      const value = event.target.value;
                      set((target) => {
                        const cells = target.cells[rowIndex];
                        if (cells) cells[column] = value;
                      }, `cell-${rowIndex}-${column}`);
                    }}
                    className={cn(
                      "h-7 w-full min-w-0 flex-1 rounded border border-zinc-200 px-1.5 text-xs outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-200",
                      element.header && rowIndex === 0 && "bg-zinc-50 font-semibold",
                      element.columns[column]?.align === "right" && "text-right",
                      element.columns[column]?.align === "center" && "text-center",
                    )}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
        {!readOnly ? (
          <Row className="flex-wrap">
            <button
              type="button"
              disabled={element.cells.length >= TABLE_MAX_ROWS}
              onClick={() => set((target) => void insertTableRow(target))}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 px-2 text-xs hover:bg-zinc-50 disabled:opacity-40"
            >
              <Rows3 className="size-3.5" /> <Plus className="size-3" /> Zeile
            </button>
            <button
              type="button"
              disabled={columns >= TABLE_MAX_COLUMNS}
              onClick={() => set((target) => void insertTableColumn(target))}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 px-2 text-xs hover:bg-zinc-50 disabled:opacity-40"
            >
              <Columns3 className="size-3.5" /> <Plus className="size-3" /> Spalte
            </button>
            <button
              type="button"
              onClick={() => setPasteOpen((open) => !open)}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 px-2 text-xs hover:bg-zinc-50"
            >
              <ClipboardPaste className="size-3.5" /> Aus Excel einfügen
            </button>
          </Row>
        ) : null}
        {pasteOpen ? (
          <div className="space-y-1.5">
            <Textarea
              value={pasteText}
              onChange={(event) => setPasteText(event.target.value)}
              rows={5}
              placeholder={
                "Zellen aus Excel, Numbers oder Google Tabellen einfügen – oder CSV:\nMonat;Umsatz\nJanuar;1.234,50 €"
              }
              className="font-mono text-xs"
              aria-label="Tabelle aus Excel oder CSV"
            />
            <p className="text-[11px] text-zinc-500">
              Ersetzt den Inhalt. Spalten mit Zahlen (auch „1.234,50 €“) werden rechtsbündig.
            </p>
            <Row>
              <button
                type="button"
                onClick={applyPaste}
                className="h-7 rounded-md bg-brand-600 px-2.5 text-xs font-medium text-white hover:bg-brand-700"
              >
                Übernehmen
              </button>
              <button
                type="button"
                onClick={() => setPasteOpen(false)}
                className="h-7 rounded-md px-2 text-xs text-zinc-600 hover:bg-zinc-100"
              >
                Abbrechen
              </button>
            </Row>
          </div>
        ) : (
          <p className="text-[11px] text-zinc-500">
            Tipp: Zellen aus Excel direkt in ein Feld einfügen. Enter springt eine Zeile tiefer.
          </p>
        )}
      </Section>
      <Section title="Tabellenstil">
        <Toggle
          checked={element.header}
          onChange={(header) => set((target) => (target.header = header))}
          label="Kopfzeile"
        />
        <Toggle
          checked={element.zebra}
          onChange={(zebra) => set((target) => (target.zebra = zebra))}
          label="Zebrastreifen"
        />
        <FontPicker
          value={element.fontFamily}
          onChange={(family) => set((target) => (target.fontFamily = family))}
        />
        <Row>
          <NumberField
            label="Größe"
            unit="px"
            min={4}
            max={500}
            value={element.fontSize}
            onChange={(value) =>
              set((target) => (target.fontSize = Math.min(500, Math.max(4, value))), "size")
            }
          />
          <NumberField
            label="Innenabstand"
            unit="px"
            min={0}
            max={200}
            value={element.padding}
            onChange={(value) =>
              set((target) => (target.padding = Math.min(200, Math.max(0, value))), "padding")
            }
          />
        </Row>
        <Row>
          <NumberField
            label="Linie"
            unit="px"
            min={0}
            max={50}
            value={element.borderWidth}
            onChange={(value) =>
              set((target) => (target.borderWidth = Math.min(50, Math.max(0, value))), "border")
            }
          />
          <NumberField
            label="Zeilenhöhe min."
            unit="px"
            min={0}
            max={2000}
            value={element.rowHeight}
            onChange={(value) =>
              set((target) => (target.rowHeight = Math.min(2000, Math.max(0, value))), "row")
            }
          />
        </Row>
        <ColorRow label="Text">
          <ColorButton
            value={element.color}
            onChange={(color) => set((target) => (target.color = color))}
            brandLink={link("color")}
          />
        </ColorRow>
        {element.header ? (
          <>
            <ColorRow label="Kopfzeile">
              <ColorButton
                value={element.headerFill}
                onChange={(color) => set((target) => (target.headerFill = color))}
                brandLink={link("fill")}
              />
            </ColorRow>
            <ColorRow label="Kopf-Text">
              <ColorButton
                value={element.headerColor}
                onChange={(color) => set((target) => (target.headerColor = color))}
              />
            </ColorRow>
          </>
        ) : null}
        <ColorRow label="Zellen">
          <ColorButton
            value={element.bodyFill}
            onChange={(color) => set((target) => (target.bodyFill = color))}
          />
        </ColorRow>
        {element.zebra ? (
          <ColorRow label="Streifen">
            <ColorButton
              value={element.stripeFill}
              onChange={(color) => set((target) => (target.stripeFill = color))}
            />
          </ColorRow>
        ) : null}
        {element.borderWidth > 0 ? (
          <ColorRow label="Linien">
            <ColorButton
              value={element.borderColor}
              onChange={(color) => set((target) => (target.borderColor = color))}
              brandLink={link("stroke")}
            />
          </ColorRow>
        ) : null}
        <Row>
          <span className="w-20 shrink-0 text-xs text-zinc-500">Spaltenbreite</span>
          <div className="flex flex-1 gap-1">
            {element.columns.map((column, index) => (
              <input
                // biome-ignore lint/suspicious/noArrayIndexKey: columns have no identity
                key={index}
                type="number"
                min={1}
                max={100}
                step={1}
                value={Math.round(column.width * 10) / 10}
                title={`Relative Breite von Spalte ${index + 1}`}
                disabled={readOnly}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  if (!Number.isFinite(value) || value <= 0) return;
                  set((target) => {
                    const col = target.columns[index];
                    if (col) col.width = Math.min(100, value);
                  }, `width-${index}`);
                }}
                className="h-7 w-full min-w-0 rounded border border-zinc-200 px-1 text-center text-xs"
              />
            ))}
          </div>
        </Row>
      </Section>
    </>
  );
}

function ColorRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Row>
      <span className="w-20 shrink-0 text-xs text-zinc-500">{label}</span>
      {children}
    </Row>
  );
}
